import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GridsterComponent, GridsterConfig, GridsterItem } from 'angular-gridster2';
import 'rxjs/add/observable/timer';
import * as moment from 'moment';

import { dashboardOptions, dynamicComponents, dynamicComponentsInterfaces } from './dashboard.config';
import { DashboardInboundWidgets, getWidget, inboundDashboard } from './dashboard.layout';
import { DashboardService } from './dashboard.service';
import { DashboardType } from './dashboard.type';

@Component({
  selector: 'cc-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild('gridster') gridster: GridsterComponent;
  public options: GridsterConfig = dashboardOptions;
  public dashboard: Array<GridsterItem> = inboundDashboard;
  public dynamicComponents = dynamicComponents;

  private dashboardRefreshInterval;
  private dashboardStatsRefreshInterval;
  private average = (array, prop) => array.reduce((sum, value) => sum + (value[prop] || 0), 0) / array.length;

  constructor(private dashboardService: DashboardService) {}

  ngOnInit() {
    // TODO: check a better way to determine gridster is ready to draw e.g. a chart to avoid glitches
    // timeout is matched to the transition delay length to be a little bit less

    this.updateInboundLists();
    this.dashboardRefreshInterval = setInterval(() => {
      this.updateInboundLists();
    }, 3000);

    this.updateInboundStats();
    this.dashboardStatsRefreshInterval = setInterval(() => {
      this.updateInboundStats();
    }, 60000);
  }

  ngOnDestroy() {
    clearInterval(this.dashboardRefreshInterval);
    clearInterval(this.dashboardStatsRefreshInterval);
  }

  updateInboundLists() {
    this.dashboardService.getInboundQueue().subscribe(callQueue => {
      this.updateWidgetValue(DashboardInboundWidgets.call_queue, callQueue);
    });

    this.dashboardService.getWorkingUsersInbound().subscribe(inboundUsers => {
      this.updateWidgetValue(DashboardInboundWidgets.users, inboundUsers);
    });
  }

  updateInboundStats() {
    this.dashboardService.getCallVolume(DashboardType.INBOUND).subscribe((results: any) => {
      this.updateWidgetValue(DashboardInboundWidgets.inbound_calls, {
        labelAll: 'INBOUND_DASHBOARD.INBOUND_CALLS',
        labelAnswered: 'INBOUND_DASHBOARD.ANSWERED_CALLS',
        data: {
          all: results.inbound_graph
            .map((stat: any) => ({
              count: stat.allCalls,
              date: stat.date
            }))
            .sort((a: any, b: any) => moment(a.date).diff(moment(b.date)))
            .map((stat: any) => ({
              ...stat,
              date: moment(stat.date).format()
            })),
          answered: results.inbound_graph
            .map((stat: any) => ({
              count: stat.answeredCalls,
              date: stat.date
            }))
            .sort((a: any, b: any) => moment(a.date).diff(moment(b.date)))
            .map((stat: any) => ({
              ...stat,
              date: moment(stat.date).format()
            })),
          total: {
            calls: results.inbound_total.calls,
            duration: results.inbound_total.duration,
            answeredCalls: results.inbound_total.answeredCalls
          },
          numbers: results.inbound_numbers.map((stat: any) => ({
            label: stat.label,
            msisdn: stat.msisdn,
            isoCountry: stat.isoCountry,
            allCalls: stat.allCalls,
            answeredCalls: stat.answeredCalls,
            callsDuration: stat.callsDuration
          }))
        }
      });
    });

    this.dashboardService.getCallVolume(DashboardType.OUTBOUND).subscribe((results: any) => {
      this.updateWidgetValue(DashboardInboundWidgets.outbound_calls, {
        labelAll: 'INBOUND_DASHBOARD.OUTBOUND_CALLS',
        labelAnswered: 'INBOUND_DASHBOARD.ANSWERED_CALLS',
        data: {
          all: results.outbound_graph
            .map((stat: any) => ({
              count: stat.allCalls,
              date: stat.date
            }))
            .sort((a: any, b: any) => moment(a.date).diff(moment(b.date)))
            .map((stat: any) => ({
              ...stat,
              date: moment(stat.date).format()
            })),
          answered: results.outbound_graph
            .map((stat: any) => ({
              count: stat.answeredCalls,
              date: stat.date
            }))
            .sort((a: any, b: any) => moment(a.date).diff(moment(b.date)))
            .map((stat: any) => ({
              ...stat,
              date: moment(stat.date).format()
            })),
          total: {
            calls: results.outbound_total.calls,
            duration: results.outbound_total.duration,
            answeredCalls: results.outbound_total.answeredCalls
          },
          agents: results.outbound_agents.map((stat: any) => ({
            agent: stat.agent,
            allCalls: stat.allCalls,
            answeredCalls: stat.answeredCalls,
            callsDuration: stat.callsDuration
          }))
        }
      });
    });

    this.dashboardService.getInboundStats().subscribe((res: any[]) => {
      this.updateWidgetValue(DashboardInboundWidgets.abandonment_rate, {
        value:
          (this.average(res, 'callsToday') - this.average(res, 'callsAnsweredToday')) /
          this.average(res, 'callsToday') *
          100,
        valueYesterday:
          (this.average(res, 'callsYesterday') - this.average(res, 'callsAnsweredYesterday')) /
          this.average(res, 'callsYesterday') *
          100
      });
      this.updateWidgetValue(DashboardInboundWidgets.sla, {
        value: this.average(res, 'serviceLevelToday'),
        valueYesterday: this.average(res, 'serviceLevelYesterday')
      });
    });
  }

  updateWidgetValue(dashboardWidget: DashboardInboundWidgets, value: dynamicComponentsInterfaces) {
    const widget = getWidget(this.dashboard, dashboardWidget);
    const inputs = widget && widget.inputs;
    if (inputs) {
      inputs.results.next(value);
    }
  }

  changeSizeWidget(widget: DashboardInboundWidgets, size: number) {
    getWidget(this.dashboard, widget).rows = size;
    this.options.api.optionsChanged();
  }
}
